<div class="npdc-title">
  <div id="titleContainer">
    <h1>{{ this.sample().content.sampleName }}</h1>
  </div>

  <div class="npdc-title__actions">
    <button mat-button class="npdc-toolbar-button" (click)="close()">Close</button>
  </div>
</div>

<div class="npdc-layout__content">
  <mat-stepper orientation="vertical" [linear]="isLinear()">
    <!-- General Details -->
    <mat-step>
      <form id="general-form" [formGroup]="generalForm" (ngSubmit)="submit()">
        <ng-template matStepLabel>General</ng-template>

        <p>
          <mat-slide-toggle [checked]="isDraft()" (toggleChange)="toggleDarft()">
            Draft
          </mat-slide-toggle>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Lithology</mat-label>
            <input matInput type="text" [formControl]="lithology" />
          </mat-form-field>
        </p>
        <p>
          <app-form-control-markdown label="Description" [formControl]="description" />
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Expedition</mat-label>
            <mat-select [formControl]="expedition">
              @for (expd of expeditionList; track expd) {
                <mat-option value="{{ expd }}">{{ expd }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Year collected</mat-label>
            <input matInput type="text" [formControl]="collectedYear" />
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Original sample name</mat-label>
            <input matInput type="text" [formControl]="originalSampleName" />
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Cabinet reference</mat-label>
            <input matInput type="text" [formControl]="cabinetRef" />
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Thin section reference</mat-label>
            <input matInput type="text" [formControl]="thinSectionRef" />
          </mat-form-field>
        </p>
        <p>
          <mat-slide-toggle [checked]="isFossil()" (toggleChange)="toggleFossil()">
            Fossil
          </mat-slide-toggle>
        </p>

        <button mat-flat-button type="submit">Save</button>
      </form>
    </mat-step>

    <!-- Geologist step -->
    <mat-step>
      <ng-template matStepLabel>Geologists</ng-template>

      <p>
        @if (!geologistList.selectedOptions.selected.length) {
          <button mat-flat-button (click)="addGeologist()">Add</button>
        } @else {
          <button mat-button (click)="removeGeologist(geologistList.selectedOptions.selected)">
            Delete
          </button>
        }
      </p>

      <mat-selection-list #geologistList>
        @if (sample().content.geologists && sample().content.geologists?.length) {
          @for (geologist of sample().content.geologists; track geologist; let isLast = $last) {
            <mat-list-option togglePosition="before" [value]="geologist">
              <span matListItemTitle>
                {{ geologist }}
              </span>
            </mat-list-option>
          }
        }
      </mat-selection-list>
    </mat-step>

    <!-- Geographic Location -->
    <mat-step>
      <form id="geo-form" [formGroup]="geoForm" (ngSubmit)="submit()">
        <ng-template matStepLabel>Geographical coverage</ng-template>
        <p>
          <button type="button" mat-button (click)="placeLookup()">
            <mat-icon>feature_search</mat-icon>
            Placename lookup
          </button>
        </p>

        <p>
          <mat-form-field appearance="outline">
            <mat-label>Placename</mat-label>
            <input matInput type="text" [formControl]="placename" />
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Longitude</mat-label>
            <input matInput type="number" min="-180" max="180" [formControl]="longitude" />
            <mat-hint>Longitude in WGS-84 decimal degrees (value between -180 and 180).</mat-hint>
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Latitude</mat-label>
            <input matInput type="number" min="-90" max="90" [formControl]="latitude" />
            <mat-hint>Latitude in WGS-84 decimal degrees (value between -90 and 90).</mat-hint>
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Altitude</mat-label>
            <input matInput type="number" [formControl]="altitude" />
            <mat-hint>Altitude relative to sea level in meter.</mat-hint>
          </mat-form-field>
        </p>

        <p>
          <mat-form-field appearance="outline">
            <mat-label>Position Accuracy</mat-label>
            <mat-select [formControl]="positionAccuracy">
              @for (opt of accuracyOpts | keyvalue; track opt.value) {
                <mat-option value="{{ opt.key }}">{{ opt.value }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </p>

        <button mat-flat-button type="submit">Save</button>
      </form>
    </mat-step>

    <!-- Link step -->
    <mat-step>
      <ng-template matStepLabel>Links</ng-template>

      <p>
        @if (!linkList.selectedOptions.selected.length) {
          <button mat-flat-button (click)="addLink()">Add</button>
        } @else {
          @if (linkList.selectedOptions.selected.length === 1) {
            <button mat-flat-button (click)="editLink(linkList.selectedOptions.selected[0])">
              Edit
            </button>
          }
          <button mat-button (click)="removeLink(linkList.selectedOptions.selected)">Delete</button>
        }
      </p>

      <mat-selection-list #linkList>
        @if (sample().content.links && sample().content.links?.length) {
          @for (link of sample().content.links; track link; let isLast = $last) {
            <mat-list-option togglePosition="before" [value]="link">
              <span matListItemTitle>
                <a appStopPropagation href="{{ link.href }}" target="_blank">{{
                  link.title || link.href
                }}</a>
              </span>
              <span matListItemLine> <b>Relation: </b>{{ link.rel }} </span>
            </mat-list-option>
          }
        }
      </mat-selection-list>
    </mat-step>

    <!-- Sample Image Upload -->
    <mat-step>
      <ng-template matStepLabel>Images</ng-template>
      <input
        #imageInput
        type="file"
        title="blob"
        name="blob"
        placeholder="Select one of more images to Upload"
        multiple
        hidden
        (change)="fileUpload($event, imagePrefix)"
      />

      @if (sampleList.selectedOptions.selected.length) {
        <p>
          <b>Note:</b> When editing image information avoid updating the 'prefix' value unless you
          really know what you are doing. Changing the 'prefix' might cause images to not show up as
          expected.
        </p>
      } @else {
        <p>Upload any images you want to be shown on the sample page.</p>
      }

      <p>
        @if (!sampleList.selectedOptions.selected.length) {
          <button mat-flat-button (click)="imageInput.click()">Upload image</button>
        } @else {
          <button
            mat-flat-button
            (click)="fileEdit(imageList, sampleList.selectedOptions.selected)"
          >
            Edit
          </button>
          @if (
            authService.user() &&
            (permissions().mayDelete ||
              authService.canInvoke(authService.endpoints.deleteGeologicalSample))
          ) {
            <button mat-button (click)="fileDelete(imageList, sampleList.selectedOptions.selected)">
              Delete
            </button>
          }
        }
      </p>

      <mat-selection-list #sampleList>
        @for (img of imageList.value()?.items; track img.id) {
          <mat-list-option togglePosition="before" [value]="img.id">
            <span matListItemTitle>{{ img.title || img.filename }}</span>
            <span matListItemLine>
              <strong>Prefix:</strong>
              {{ img.prefix }}
            </span>
          </mat-list-option>
        }
      </mat-selection-list>

      <p>
        <mat-paginator
          showFirstLastButtons
          (page)="imagePageChange($event)"
          [length]="imageCount()"
          [pageIndex]="imagePage()"
          [pageSize]="imagePageSize()"
          [pageSizeOptions]="[imageDefaultPage, 10, 20, 50]"
        />
      </p>
    </mat-step>

    <!-- File Upload -->
    <mat-step>
      <ng-template matStepLabel>Files</ng-template>
      <input
        #fileInput
        type="file"
        title="blob"
        name="blob"
        placeholder="Select one of more files to Upload"
        multiple
        hidden
        (change)="fileUpload($event, filePrefix)"
      />

      @if (attachmentList.selectedOptions.selected.length) {
        <p>
          <b>Note:</b> When editing file information avoid updating the 'prefix' value unless you
          really know what you are doing. Changing the 'prefix' might cause files to not show up as
          expected.
        </p>
      } @else {
        <p>Upload files relevant to the sample.</p>
      }

      <p>
        @if (!attachmentList.selectedOptions.selected.length) {
          <button mat-flat-button (click)="fileInput.click()">Upload File</button>
        } @else {
          <button
            mat-flat-button
            (click)="fileEdit(fileList, attachmentList.selectedOptions.selected)"
          >
            Edit
          </button>
          @if (
            authService.user() &&
            (permissions().mayDelete ||
              authService.canInvoke(authService.endpoints.deleteGeologicalSample))
          ) {
            <button
              mat-button
              (click)="fileDelete(fileList, attachmentList.selectedOptions.selected)"
            >
              Delete
            </button>
          }
        }
      </p>

      <mat-selection-list #attachmentList>
        @for (file of fileList.value()?.items; track file.id) {
          <mat-list-option togglePosition="before" [value]="file.id">
            <span matListItemTitle>{{ file.title || file.filename }}</span>
            <span matListItemLine>
              <strong>Prefix:</strong>
              {{ file.prefix }}
            </span>
          </mat-list-option>
        }
      </mat-selection-list>

      <p>
        <mat-paginator
          showFirstLastButtons
          (page)="filePageChange($event)"
          [length]="fileCount()"
          [pageIndex]="filePage()"
          [pageSize]="filePageSize()"
          [pageSizeOptions]="[fileDefaultPage, 10, 20, 50]"
        />
      </p>
    </mat-step>
  </mat-stepper>
</div>
